import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button, Select, Space, Table, Tag, Typography,
} from 'antd';
import {
  ResponsiveContainer, Sankey, Tooltip,
} from 'recharts';
import {
  SEGMENTS, getSegmentColor, getSegmentSortIndex,
} from '../constants';

const { Text, Title } = Typography;

const nodeId = (side, label) => `${side}:${label}`;

const SegmentTag = ({ label }) => {
  const color = getSegmentColor(label);
  if (!label) return <Tag>—</Tag>;
  return color ? <Tag color={color}>{label}</Tag> : <Tag>{label}</Tag>;
};

SegmentTag.propTypes = {
  label: PropTypes.string,
};

SegmentTag.defaultProps = {
  label: null,
};

const hexToRgba = (hex, alpha) => {
  if (!hex || typeof hex !== 'string' || !hex.startsWith('#')) {
    return `rgba(140, 140, 140, ${alpha})`;
  }
  const raw = hex.slice(1);
  const full = raw.length === 3
    ? raw.split('').map((c) => `${c}${c}`).join('')
    : raw;
  if (full.length !== 6) return `rgba(140, 140, 140, ${alpha})`;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const SankeyNode = (props) => {
  const {
    x, y, width, height, payload,
  } = props;
  const isLeft = payload.side === 'from' || payload.depth === 0;
  const color = payload.color || getSegmentColor(payload.label) || '#8c8c8c';
  const labelX = isLeft ? x - 8 : x + width + 8;
  return (
    <g>
      <rect
        x={x}
        y={y}
        width={width}
        height={Math.max(height, 1)}
        fill={color}
        rx={2}
      />
      <text
        x={labelX}
        y={y + height / 2}
        textAnchor={isLeft ? 'end' : 'start'}
        dominantBaseline="middle"
        fontSize={12}
        fill="rgba(0, 0, 0, 0.85)"
      >
        {payload.label}
      </text>
    </g>
  );
};

SankeyNode.propTypes = {
  x: PropTypes.number,
  y: PropTypes.number,
  width: PropTypes.number,
  height: PropTypes.number,
  // eslint-disable-next-line react/forbid-prop-types
  payload: PropTypes.object,
};

SankeyNode.defaultProps = {
  x: 0,
  y: 0,
  width: 0,
  height: 0,
  payload: {},
};

const SankeyLink = (props) => {
  const {
    sourceX,
    targetX,
    sourceY,
    targetY,
    sourceControlX,
    targetControlX,
    linkWidth,
    payload,
    selected,
  } = props;
  const sourceColor = payload?.source?.color
    || getSegmentColor(payload?.from)
    || '#8c8c8c';
  const opacity = selected ? 0.75 : 0.4;
  return (
    <path
      d={`
        M${sourceX},${sourceY}
        C${sourceControlX},${sourceY}
         ${targetControlX},${targetY}
         ${targetX},${targetY}
      `}
      fill="none"
      stroke={hexToRgba(sourceColor, opacity)}
      strokeWidth={Math.max(linkWidth, 1)}
      style={{ cursor: 'pointer' }}
    />
  );
};

SankeyLink.propTypes = {
  sourceX: PropTypes.number,
  targetX: PropTypes.number,
  sourceY: PropTypes.number,
  targetY: PropTypes.number,
  sourceControlX: PropTypes.number,
  targetControlX: PropTypes.number,
  linkWidth: PropTypes.number,
  // eslint-disable-next-line react/forbid-prop-types
  payload: PropTypes.object,
  selected: PropTypes.bool,
};

SankeyLink.defaultProps = {
  sourceX: 0,
  targetX: 0,
  sourceY: 0,
  targetY: 0,
  sourceControlX: 0,
  targetControlX: 0,
  linkWidth: 1,
  payload: {},
  selected: false,
};

/** Strip `from:` / `to:` prefix from Sankey node names when label is missing. */
const displayLabel = (node) => {
  if (!node) return '';
  if (node.label) return node.label;
  const name = String(node.name || '');
  const colon = name.indexOf(':');
  return colon >= 0 ? name.slice(colon + 1) : name;
};

const SankeyTooltipContent = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const entry = payload[0];
  // Sankey wraps the graphical item as entry.payload; link/node data is on that
  // item's own .payload (see recharts getPayloadOfTooltip).
  const graphical = entry.payload || {};
  const data = graphical.payload || graphical;

  if (data.source && data.target) {
    const from = data.from || displayLabel(data.source);
    const to = data.to || displayLabel(data.target);
    const count = data.value ?? entry.value;
    return (
      <div className="segmentation-sankey-tooltip">
        <div>
          <Text strong>
            {from}
            {' → '}
            {to}
          </Text>
        </div>
        <div>
          {`${count} ${count === 1 ? 'customer' : 'customers'}`}
        </div>
        <div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Click to list customers
          </Text>
        </div>
      </div>
    );
  }

  const label = data.label || displayLabel(data) || entry.name;
  if (!label && entry.value == null) return null;
  return (
    <div className="segmentation-sankey-tooltip">
      {label ? <div><Text strong>{label}</Text></div> : null}
      {entry.value != null ? <div>{`${entry.value} customers`}</div> : null}
    </div>
  );
};

SankeyTooltipContent.propTypes = {
  active: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types
  payload: PropTypes.array,
};

SankeyTooltipContent.defaultProps = {
  active: false,
  payload: [],
};

const buildSankeyData = (transitions, focusLabels) => {
  const focusSet = focusLabels.length ? new Set(focusLabels) : null;
  const filtered = transitions.filter((row) => {
    if (!row.from || !row.to || !row.count) return false;
    if (!focusSet) return true;
    return focusSet.has(row.from) || focusSet.has(row.to);
  });

  const fromLabels = [...new Set(filtered.map((r) => r.from))]
    .sort((a, b) => getSegmentSortIndex(a) - getSegmentSortIndex(b));
  const toLabels = [...new Set(filtered.map((r) => r.to))]
    .sort((a, b) => getSegmentSortIndex(a) - getSegmentSortIndex(b));

  const nodes = [
    ...fromLabels.map((label) => ({
      name: nodeId('from', label),
      label,
      side: 'from',
      color: getSegmentColor(label) || '#8c8c8c',
    })),
    ...toLabels.map((label) => ({
      name: nodeId('to', label),
      label,
      side: 'to',
      color: getSegmentColor(label) || '#8c8c8c',
    })),
  ];
  const indexByName = {};
  nodes.forEach((n, i) => { indexByName[n.name] = i; });

  const links = filtered.map((row) => ({
    source: indexByName[nodeId('from', row.from)],
    target: indexByName[nodeId('to', row.to)],
    value: Number(row.count) || 0,
    from: row.from,
    to: row.to,
  })).filter((link) => (
    link.source != null && link.target != null && link.value > 0
  ));

  return { nodes, links };
};

const SegmentSankey = ({
  transitions, moved, changedCount, repTransitions,
}) => {
  const [focus, setFocus] = useState([]);
  const [selected, setSelected] = useState(null);
  const [rep, setRep] = useState(null);

  const repOptions = useMemo(() => {
    if (!repTransitions.length) return [];
    const names = [];
    repTransitions.forEach((row) => {
      if (row?.rep && !names.includes(row.rep)) names.push(row.rep);
    });
    names.sort((a, b) => String(a).localeCompare(String(b)));
    return names.map((name) => ({ label: name, value: name }));
  }, [repTransitions]);

  const chartTransitions = useMemo(() => {
    if (!rep) return transitions;
    return repTransitions
      .filter((row) => row && row.rep === rep && row.from && row.to && row.count)
      .map((row) => ({
        from: row.from,
        to: row.to,
        count: Number(row.count) || 0,
      }));
  }, [rep, repTransitions, transitions]);

  const sankeyData = useMemo(
    () => buildSankeyData(chartTransitions, focus),
    [chartTransitions, focus],
  );

  const listedCustomers = useMemo(() => {
    const focusSet = focus.length ? new Set(focus) : null;
    if (!selected && !focusSet) return [];
    return moved
      .filter((row) => {
        if (rep && row.salesRep !== rep) return false;
        if (focusSet && !focusSet.has(row.from) && !focusSet.has(row.to)) return false;
        if (selected && (row.from !== selected.from || row.to !== selected.to)) return false;
        return true;
      })
      .sort((a, b) => {
        const nameA = (a.customerName || a.customerId || '').toLowerCase();
        const nameB = (b.customerName || b.customerId || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });
  }, [moved, selected, rep, focus]);

  const selectedFlowCount = useMemo(() => {
    if (!selected) return 0;
    const hit = chartTransitions.find(
      (row) => row.from === selected.from && row.to === selected.to,
    );
    return hit?.count ?? listedCustomers.length;
  }, [selected, chartTransitions, listedCustomers.length]);

  const focusFlowCount = useMemo(() => {
    if (!focus.length) return 0;
    const focusSet = new Set(focus);
    return chartTransitions.reduce((sum, row) => {
      if (!focusSet.has(row.from) && !focusSet.has(row.to)) return sum;
      return sum + (Number(row.count) || 0);
    }, 0);
  }, [chartTransitions, focus]);

  const toggleFocus = (label) => {
    const next = focus.includes(label)
      ? focus.filter((value) => value !== label)
      : [...focus, label];
    setFocus(next);
    if (
      selected
      && next.length
      && !next.includes(selected.from)
      && !next.includes(selected.to)
    ) {
      setSelected(null);
    }
  };

  const onSankeyClick = (item, type) => {
    if (type !== 'link' || !item) return;
    const payload = item.payload || item;
    const from = payload.from
      || payload.source?.label
      || item.from
      || item.source?.label;
    const to = payload.to
      || payload.target?.label
      || item.to
      || item.target?.label;
    if (!from || !to) return;
    setSelected((prev) => (
      prev && prev.from === from && prev.to === to
        ? null
        : { from, to }
    ));
  };

  if (!transitions.length) return null;

  const emptyMovementText = rep && focus.length === 0
    ? 'No movements for this sales rep.'
    : 'No movements match the selected focus segments.';

  const onRepChange = (value) => {
    setRep(value || null);
    setSelected(null);
  };

  const linkContent = (linkProps) => {
    const from = linkProps.payload?.from;
    const to = linkProps.payload?.to;
    const isSelected = !!(
      selected && selected.from === from && selected.to === to
    );
    return <SankeyLink {...linkProps} selected={isSelected} />;
  };

  return (
    <div className="segmentation-sankey">
      <div className="segmentation-sankey-head">
        <Text strong>Customer movements</Text>
        <Text type="secondary">
          Live segment (left) → this run (right). Click a focus segment or a flow
          for the customer list.
        </Text>
      </div>

      {repOptions.length > 0 && (
        <div className="segmentation-sankey-rep">
          <Text type="secondary">Sales rep</Text>
          <Select
            showSearch
            allowClear
            placeholder="All reps"
            optionFilterProp="label"
            style={{ width: 240 }}
            value={rep || undefined}
            onChange={onRepChange}
            options={repOptions}
          />
        </div>
      )}

      <div className="segmentation-sankey-chips">
        <Text type="secondary" style={{ marginRight: 8 }}>Focus:</Text>
        <Space wrap size={[4, 8]}>
          {SEGMENTS.map((segment) => {
            const checked = focus.includes(segment.label);
            return (
              <Tag.CheckableTag
                key={segment.key}
                checked={checked}
                onChange={() => toggleFocus(segment.label)}
                style={checked ? {
                  backgroundColor: segment.color,
                  borderColor: segment.color,
                  color: '#fff',
                } : {
                  borderColor: segment.color,
                  color: segment.color,
                }}
              >
                {segment.label}
              </Tag.CheckableTag>
            );
          })}
          {focus.length > 0 && (
            <Button type="link" size="small" onClick={() => setFocus([])}>
              Clear focus
            </Button>
          )}
        </Space>
      </div>

      {sankeyData.links.length === 0 ? (
        <Text type="secondary">
          {emptyMovementText}
        </Text>
      ) : (
        <div className="segmentation-sankey-chart">
          <ResponsiveContainer width="100%" height="100%">
            <Sankey
              data={sankeyData}
              nodeWidth={12}
              nodePadding={24}
              linkCurvature={0.5}
              iterations={32}
              margin={{
                top: 8, right: 96, bottom: 8, left: 96,
              }}
              node={<SankeyNode />}
              link={linkContent}
              onClick={onSankeyClick}
              sort={false}
            >
              <Tooltip
                content={<SankeyTooltipContent />}
                cursor={false}
              />
            </Sankey>
          </ResponsiveContainer>
        </div>
      )}

      {(selected || focus.length > 0) && (
        <div className="segmentation-sankey-detail">
          <div className="segmentation-sankey-detail-head">
            <Title level={5} style={{ margin: 0 }}>
              {selected ? (
                <>
                  <SegmentTag label={selected.from} />
                  <Text style={{ margin: '0 8px' }}>→</Text>
                  <SegmentTag label={selected.to} />
                  <Text type="secondary" style={{ marginLeft: 8 }}>
                    {`(${selectedFlowCount})`}
                  </Text>
                </>
              ) : (
                <>
                  {focus.map((label) => (
                    <span key={label} style={{ marginRight: 4 }}>
                      <SegmentTag label={label} />
                    </span>
                  ))}
                  <Text type="secondary" style={{ marginLeft: 8 }}>
                    {`(${focusFlowCount})`}
                  </Text>
                </>
              )}
            </Title>
            <Button
              type="link"
              onClick={() => (selected ? setSelected(null) : setFocus([]))}
            >
              {selected ? 'Clear selection' : 'Clear focus'}
            </Button>
          </div>
          <Table
            size="small"
            rowKey={(row) => `${row.customerId}-${row.from}-${row.to}`}
            dataSource={listedCustomers}
            pagination={{ pageSize: 20, hideOnSinglePage: true }}
            locale={{ emptyText: 'No named customers in the capped move list.' }}
            columns={[
              {
                title: 'Customer',
                key: 'customer',
                render: (_, row) => row.customerName || row.customerId || '—',
              },
              {
                title: 'From',
                dataIndex: 'from',
                key: 'from',
                render: (value) => <SegmentTag label={value} />,
              },
              {
                title: 'To',
                dataIndex: 'to',
                key: 'to',
                render: (value) => <SegmentTag label={value} />,
              },
            ]}
          />
          {changedCount > moved.length && (
            <Text type="secondary">
              {`Named list capped at ${moved.length} of ${changedCount} total moves.`}
            </Text>
          )}
          {selected && selectedFlowCount > listedCustomers.length && (
            <Text type="secondary">
              {`Showing ${listedCustomers.length} of ${selectedFlowCount} `
                + 'customers for this flow (remainder outside the capped list).'}
            </Text>
          )}
          {!selected && focusFlowCount > listedCustomers.length && (
            <Text type="secondary">
              {`Showing ${listedCustomers.length} of ${focusFlowCount} `
                + 'customers for this focus (remainder outside the capped list).'}
            </Text>
          )}
        </div>
      )}
    </div>
  );
};

SegmentSankey.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  transitions: PropTypes.array,
  // eslint-disable-next-line react/forbid-prop-types
  moved: PropTypes.array,
  changedCount: PropTypes.number,
  // eslint-disable-next-line react/forbid-prop-types
  repTransitions: PropTypes.array,
};

SegmentSankey.defaultProps = {
  transitions: [],
  moved: [],
  changedCount: 0,
  repTransitions: [],
};

export default SegmentSankey;
